import { ComponentFactoryResolver, ComponentRef, Directive, ElementRef, forwardRef, Host, Inject, Optional, Renderer2, ViewContainerRef } from '@angular/core';
import { ControlContainer, FormControlDirective, FormControlName, FormGroupDirective, NgControl } from '@angular/forms';
import { convertCamelCasesToSpaces, RxjsService, UI_COMPONENTS_TO_CONSIDER_TITLE_FIELD_AS_NAME } from '@app/shared';
import { ControlErrorComponent, ControlErrorContainerDirective, FormSubmitDirective } from '@app/shared/directives/form-control-errors';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { EMPTY, fromEvent, iif, merge, Observable } from 'rxjs';
import { FORM_ERRORS } from './form-errors';

export const formDirectiveProvider: any = {
  provide: ControlContainer,
  useExisting: forwardRef(() => FormGroupDirective)
};

@Directive({
  selector: `[formControl]:not([actualDirective]), [formControlName]:not([actualDirective]), button[type="button"][formArrayCreateNew]`,
  providers: [formDirectiveProvider]
})
export class ControlErrorsDirective {
  ref: ComponentRef<ControlErrorComponent>;
  container: ViewContainerRef;
  parentFormSubmit$: Observable<Event>;
  parentFormReset$: Observable<Event>;

  constructor(
    private elementRef: ElementRef<HTMLFormElement>,
    private vcr: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    @Optional() controlErrorContainer: ControlErrorContainerDirective,
    @Inject(FORM_ERRORS) private errors,
    private rxjsService: RxjsService, @Optional() @Host() private form: FormSubmitDirective,
    @Optional() private controlDir: NgControl, private renderer: Renderer2) {
    this.container = controlErrorContainer ? controlErrorContainer.vcr : vcr;
  }

  // filter the exact targeted element in nested children and its ancestors 
  // add here the exact targeted element to be returned (put a debugger and make sure what is returning)
  // if the targeted element returns a wrong one then the streaming will not happen (i.e input element is only has blur,change events etc... but not a normal div )
  get currentElement(): Element {
    if (this.nativeElementLocalName === 'select' || this.nativeElementLocalName === 'mat-radio-group') {
      if (this.elementRef.nativeElement.className.includes('mat-form-field-autofill-control cdk-text-field-autofill-monitored')) {
        return this.elementRef.nativeElement;
      }
      else {
        return this.elementRef.nativeElement;
      }
    }
    else if (this.nativeElementLocalName === 'mat-select-autocomplete-custom-control') {
      return this.elementRef.nativeElement.children[0].children[0].children[0].children[0].children[0];
    }
    else if (this.nativeElementLocalName == 'p-calendar') {
      return this.elementRef.nativeElement.children[0]?.children[0];
    }
    else if (this.nativeElementLocalName == 'p-multiselect' || this.nativeElementLocalName == 'p-dropdown') {
      return this.elementRef.nativeElement.children[0].children[0].children[0];
    }
    else if (this.nativeElementLocalName == 'button') {
      return this.elementRef.nativeElement;
    }
    else {
      return this.elementRef.nativeElement.children.length === 0 ? this.elementRef.nativeElement : this.elementRef.nativeElement.children[0];
    }
  }

  // returns the element's localname such as input,select,input-custom-control,select-custom-control etc...
  get nativeElementLocalName() {
    return this.elementRef.nativeElement.localName;
  }

  ngOnInit() {
    if (this.form) {
      this.parentFormSubmit$ = iif(() => !!this.form, this.form.submitted$);
      this.parentFormReset$ = iif(() => !!this.form, this.form.reset$);
    }
  }

  ngAfterViewInit() {
    if (this.form) {
      this.dynamicErrorMessageShowingLogic();
    }
  }

  dynamicErrorMessageShowingLogic() {
    /* merge all the observable streams to make sure our integrated logics added here. 
    such as assigning / removing errors to specific fields during runtime etc..*/
    merge(
      // streaming happens on every keyup event on formControlName fields
      fromEvent(this.currentElement, 'keyup'),
      // streaming happens on every blur event on formControlName fields
      fromEvent(this.currentElement, 'blur'),
      this.control?.valueChanges ? this.control.valueChanges : EMPTY,
      this.parentFormSubmit$,
      this.parentFormReset$,
      fromEvent(this.currentElement, 'click')
    ).pipe(
      untilDestroyed(this)).subscribe((response) => {
        if (response == null || response == undefined) return;
        if (response.code !== 'Tab' && (response?.currentTarget?.localName == 'i' && response?.currentTarget?.className?.includes('icon') ||
          response?.currentTarget?.localName == 'button' && response?.currentTarget?.className?.includes('btn'))
          && response instanceof PointerEvent) {
          this.splitOutFormArraysAndFormControlsWithFocusAndBlur(response);
        }
        else {
          setTimeout(() => {
            // make sure we manually set the form change detection property to true for every field value changes
            if (response?.type !== "submit" && this.form?.element.className.includes('ng-dirty') || this.form?.element?.['nativeElement']?.className?.includes('ng-dirty')) {
              this.rxjsService.setFormChangeDetectionProperty(true);
            }
          });
          /* the below condition is the trick is to handle the form array errors hiding (i.e when the entire form is submitted and 
     after the user tries to click on the add new form array but still gets the error)*/
          if (response?.type === "submit") {
            if (response.eventPhase == Event.NONE) {
              return;
            }
          }
          if (this.controlDir) {
            let controlErrors, fieldName: any = '', name = '';
            // if the control is defined as formControlName in the form 
            if (this.controlDir instanceof FormControlName) {
              /* to make sure that if the "name" property is present then the error message will be 
              gotten from it else the error message will be gotten from the "formControlName" as default */

              /** if the nativeElementLocalName is "p-dropdown" / "p-autocomplete" then we have to map the "title" field instead of "name" field */
              name = UI_COMPONENTS_TO_CONSIDER_TITLE_FIELD_AS_NAME.includes(this.nativeElementLocalName) ? this.elementRef.nativeElement.title : this.currentElement['name'];
              if (name !== '' && name) {
                fieldName = name;
              }
              else {
                fieldName = this.controlDir.name;
              }
            }
            // if the control is defined as formControl in the form
            else if (this.controlDir instanceof FormControlDirective) {
              console.error(`Cannot handle ${this.controlDir.control} field since it is not a proper formControlName`);
            }
            if (fieldName == null) return;
            if (this.control?.errors && Object.keys(this.control?.errors).length > 0) {
              controlErrors = this.control.errors;
            }
            if (controlErrors && fieldName && response['type'] !== 'reset') {
              this.renderer.addClass(this.currentElement, 'is-invalid');
              // condition is added to make sure whether to show the error message from the field has "name" attribute or not 
              if (this.nativeElementLocalName == 'input-custom-control' || this.nativeElementLocalName == 'text-area-custom-control'
                || this.nativeElementLocalName == 'select-custom-control' || this.nativeElementLocalName == 'mat-select-autocomplete-custom-control') {
                fieldName = this.currentElement?.getAttribute('customNameAttribute') ? this.currentElement?.getAttribute('customNameAttribute') : convertCamelCasesToSpaces(fieldName);
              }
              else if (name == '' && (this.nativeElementLocalName !== 'input-custom-control' && this.nativeElementLocalName !== 'text-area-custom-control'
                && this.nativeElementLocalName !== 'select-custom-control' && this.nativeElementLocalName !== 'mat-select-autocomplete-custom-control')) {
                fieldName = convertCamelCasesToSpaces(fieldName);
              }
              let firstLetterUppercase = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
              const firstKey = Object.keys(controlErrors)[0];
              if (typeof controlErrors[firstKey] === 'object') {
                controlErrors[firstKey].formControlName = firstLetterUppercase;
              }
              else if (controlErrors) {
                /* to make sure we do a deep copy of the "controlErrors" object to prepare the form-errors.ts file's
                 object creation to throw errors */
                controlErrors[firstKey] = { ...controlErrors };
                controlErrors[firstKey].formControlName = firstLetterUppercase;
              }
              /* based on this property we show max length messages along with the count and excluding its masking space characters ( i.g mobile numbers, credcard numbers etc..)*/
              if (!UI_COMPONENTS_TO_CONSIDER_TITLE_FIELD_AS_NAME.includes(this.nativeElementLocalName) && this.currentElement['title']) {
                controlErrors[firstKey].customTitleForErrorMessage = this.currentElement['title'];
              }
              // if we add the below key in the respective tags (input,select etc...) then the error's prefix will be added in the errors followed by the min/max digits/other characters
              // e.g component is: http://localhost:4200/event-management/stack-operator/call-workflow-list/list?id=847ec07c-ada2-4677-9087-55ede71c7597&customerId=fe6734a8-c7cb-4763-823c-7b85b229ddea&customerAddressId=b5d5258c-2bc9-47fa-b202-3aac20690b3e&partitionId=cc7cc55b-6170-4433-a98d-858f1a3167dc&customerName=Mr%20Vignesh%20Ramesh%20Aug%201%20&customerAddress=103,%20CT,%20BERRA,%2033%20CANBERRA%20AVENUE,%20Park%20Hill,%20Durban%20North,%20Kwazulu-Natal,%204051&tab=1
              // kindly go to the quick action on the above page in click the option called "Fire" then check the actual usage
              if (this.currentElement?.['accessKey']) {
                controlErrors[firstKey]['actualErrorMessage'] = this.currentElement['accessKey'];
              }
              let getError;
              // start logic for immanuel's form-control-errors.component.ts file's error showing logic
              if (firstKey === 'Mask error') {
                getError = this.errors['maskError'];
              }
              else {
                getError = this.errors[firstKey];
              }
              // end logic for immanuel's form-control-errors.component.ts file's error showing logic
              /* added the firstKey != 'matDatepickerMin' condition because of immanuel's custom error 
               * showing for mindate error in p-calendar only on one page which is billing-financial-year-configuration-add-edit.component.html */
              if (firstKey != 'matDatepickerMin' && getError && controlErrors[firstKey]) {
                let text = getError(controlErrors[firstKey]);
                // to make sure on form submission all the fields are marked as touched. We can remove markAsTouched() manually given in all the component submissions (if any)
                this.controlDir.control.markAsTouched();
                this.setError(text);
              }
              // if no control errors are captured then remove the class and the error also
              else if (!getError && !controlErrors[firstKey]) {
                this.renderer.removeClass(this.currentElement, 'is-invalid');
                this.setError(null);
              }
            } else if (this.ref) {
              this.renderer.removeClass(this.currentElement, 'is-invalid');
              this.setError(null);
            }
          }
        }
      });
  }

  get control() {
    return this.controlDir?.control;
  }

  // responsible to create dynamic tag called <ng-component> and adds error message into it then shows in the DOM 
  setError(text: string) {
    if (!this.ref) {
      const factory = this.resolver.resolveComponentFactory(ControlErrorComponent);
      this.ref = this.container.createComponent(factory);
    }
    this.ref.instance.text = text;
  }

  // splitup the form array and form controls to focus and blur
  splitOutFormArraysAndFormControlsWithFocusAndBlur(element: PointerEvent) {
    const currentTarget: HTMLElement = (element?.currentTarget as HTMLElement);
    let nodeList: NodeListOf<Element>[] = [];
    const nestedParentNode = (currentTarget.localName == 'button') ? currentTarget.parentNode.parentNode
      : currentTarget.parentNode.parentNode.parentNode;
    if (nestedParentNode?.['getAttribute']?.("formArrayName") && (nestedParentNode?.['getAttribute']?.("formArrayName") == nestedParentNode.nextSibling?.['getAttribute']?.("formArrayName"))) {
      nodeList.push(nestedParentNode.querySelectorAll("[formControlName]"));
      nodeList.push((nestedParentNode.nextSibling as any)?.querySelectorAll("[formControlName]"));
    }
    else {
      nodeList.push(nestedParentNode.querySelectorAll("[formControlName]"));
    }
    nodeList = nodeList.filter(nodeElement => nodeElement?.length > 0);
    if (nodeList.length == 0) {
      nodeList.push(nestedParentNode.parentNode.querySelectorAll("[formControlName]"));
    }
    nodeList.forEach((nodeElement: NodeList, index) => {
      nodeElement?.forEach((element: HTMLElement) => {
        this.focusableFirstInvalidElement(element).focus();
        this.focusableFirstInvalidElement(element).blur();
      });
      for (let i = 0; i <= nodeList[0].length; i++) {
        if ((nodeList[0]?.[i]).className.includes('invalid')) {
          // to have cursor focused on the exact error field
          this.focusableFirstInvalidElement(nodeList[0]?.[i]).focus();
          break;
        }
      }
    });
  }

  focusableFirstInvalidElement(nativeInvalidControl): HTMLElement {
    const nativeInvalidControlLocalName = nativeInvalidControl.localName;
    if (nativeInvalidControlLocalName === 'select' || nativeInvalidControlLocalName === 'mat-radio-group') {
      if (nativeInvalidControl.className.includes('mat-form-field-autofill-control cdk-text-field-autofill-monitored')) {
        return nativeInvalidControl;
      }
      else {
        return nativeInvalidControl;
      }
    }
    else if (nativeInvalidControlLocalName === 'mat-select-autocomplete-custom-control') {
      return nativeInvalidControl.children[0].children[0].children[0].children[0].children[0];
    }
    else if (nativeInvalidControlLocalName == 'p-calendar') {
      return nativeInvalidControl.children[0]?.children[0];
    }
    else if (nativeInvalidControlLocalName == 'p-multiselect' || nativeInvalidControlLocalName == 'p-dropdown') {
      return nativeInvalidControl.children[0].children[0].children[0];
    }
    else {
      return nativeInvalidControl.children.length === 0 ? nativeInvalidControl : nativeInvalidControl.children[0];
    }
  }

  ngOnDestroy() {
  }
}